import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';

import ActivityCaptureGallery from './ActivityCaptureGallery.js';
import ActivityDimensionChips, { type ActivityDimensionBlock } from './ActivityDimensionChips.js';
import ActivityRejectDialog from './ActivityRejectDialog.js';
import ActivityReviewPriorityMarker from './ActivityReviewPriorityMarker.js';
import {
  type ActivityEvent,
  type ActivityEventTrigger,
  type ActivityPersonSighting,
  ActivityApi,
  useActivityEvents,
} from '../../api/index.js';
import Table from '../Table/Table.js';

interface ActivityEventsTableProps {
  patientId: string;
  /** Defaults to 50; the DataGrid pageSize hint matches. */
  limit?: number;
}

const triggerColor: Record<ActivityEventTrigger, 'primary' | 'default'> = {
  motion: 'primary',
  scheduled: 'default',
};

// Default range — show the last 7 days. Operators can widen/narrow before the
// first fetch fires. We deliberately bound the upper end at "now" so the
// timeline never asks the API for the future.
function defaultFrom(): DateTime {
  return DateTime.now().minus({ days: 7 }).startOf('day');
}
function defaultTo(): DateTime {
  return DateTime.now().endOf('minute');
}

const ActivityEventsTable: FC<ActivityEventsTableProps> = ({ patientId, limit = 50 }) => {
  const [from, setFrom] = useState<DateTime | null>(defaultFrom);
  const [to, setTo] = useState<DateTime | null>(defaultTo);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ActivityEvent | null>(null);
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);

  const rangeValid = !!from?.isValid && !!to?.isValid && to.diff(from, 'minutes').minutes > 0;

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useActivityEvents(
    {
      patientId,
      from: rangeValid && from ? from.toUTC().toISO() ?? '' : '',
      to: rangeValid && to ? to.toUTC().toISO() ?? '' : '',
      limit,
    },
    { enabled: rangeValid },
  );

  const flatEvents = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.items),
    [data],
  );
  const eventById = useMemo(() => {
    const m = new Map<string, ActivityEvent>();
    flatEvents.forEach((e) => m.set(e.id, e));
    return m;
  }, [flatEvents]);

  const highPriorityCount = useMemo(
    () => flatEvents.filter((event) => event.reviewPriority === 'high').length,
    [flatEvents],
  );

  const rows: GridRowsProp = useMemo(() => {
    const visibleEvents = highPriorityOnly
      ? flatEvents.filter((event) => event.reviewPriority === 'high')
      : flatEvents;
    return visibleEvents.map((event) => {
      const meta = event.metadata as { aiSuggestion?: string; reasoning?: string } | undefined;
      return {
        id: event.id,
        capturedAt: event.capturedAt,
        codeText: event.code.code,
        codeDescription: event.code.description,
        categoryColor: event.code.category?.color ?? null,
        categoryName: event.code.category?.name ?? null,
        cameraName: event.camera?.name ?? event.cameraId,
        trigger: event.trigger,
        confidence: event.confidence,
        attemptCount: event.attemptCount,
        aiSuggestion: meta?.aiSuggestion ?? null,
        reasoning: meta?.reasoning ?? null,
        dimensions: event.dimensions ?? null,
        reviewPriority: event.reviewPriority ?? 'normal',
        people: event.people ?? [],
        capturesCount: event.capturesCount ?? 0,
        videoS3Key: event.videoS3Key ?? null,
        reviewStatus: event.reviewStatus ?? null,
        reviewCorrectedCode: event.reviewLog?.code.code ?? null,
      };
    });
  }, [flatEvents, highPriorityOnly]);

  const acceptEvent = async (eventId: string) => {
    await ActivityApi.reviewEvent(eventId, { verdict: 'accepted' });
    await refetch();
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'capturedAt',
      headerName: 'Captured at',
      flex: 1.2,
      renderCell: (params) =>
        DateTime.fromISO(params.value as string).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
    },
    {
      field: 'codeText',
      headerName: 'Code',
      flex: 0.7,
      renderCell: (params) => {
        const row = params.row as { categoryColor: string | null; reviewPriority: 'high' | 'normal' };
        return (
          <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
            <Chip
              label={params.value as string}
              size={'small'}
              sx={{
                bgcolor: row.categoryColor ?? undefined,
                color: row.categoryColor ? '#fff' : undefined,
                fontFamily: 'monospace',
              }}
            />
            <ActivityReviewPriorityMarker priority={row.reviewPriority} />
          </Stack>
        );
      },
    },
    {
      field: 'codeDescription',
      headerName: 'Activity',
      flex: 1.8,
      renderCell: (params) => {
        const aiSuggestion = (params.row as { aiSuggestion: string | null }).aiSuggestion;
        const reasoning = (params.row as { reasoning: string | null }).reasoning;
        return (
          <Stack sx={{ py: 0.5, minWidth: 0 }}>
            <Typography variant={'body2'} noWrap>{params.value as string}</Typography>
            {aiSuggestion ? (
              <Typography
                variant={'caption'}
                color={'text.secondary'}
                noWrap
                sx={{ fontStyle: 'italic' }}
              >
                AI: {aiSuggestion}
              </Typography>
            ) : null}
            {reasoning ? (
              <Tooltip arrow title={reasoning}>
                <Typography variant={'caption'} color={'text.disabled'} noWrap>
                  {reasoning}
                </Typography>
              </Tooltip>
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: 'dimensions',
      headerName: 'Dimensions',
      flex: 1.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const dimensions = params.value as ActivityDimensionBlock | null;
        if (!dimensions) {
          return <Typography variant={'caption'} color={'text.disabled'}>—</Typography>;
        }
        return <ActivityDimensionChips dimensions={dimensions} />;
      },
    },
    {
      field: 'categoryName',
      headerName: 'Category',
      flex: 0.9,
      renderCell: (params) =>
        params.value ? (
          <Typography variant={'body2'} color={'text.secondary'}>
            {params.value as string}
          </Typography>
        ) : null,
    },
    {
      field: 'cameraName',
      headerName: 'Camera',
      flex: 1,
      renderCell: (params) => (
        <Typography variant={'body2'} color={'text.secondary'} noWrap>
          {params.value as string}
        </Typography>
      ),
    },
    {
      field: 'trigger',
      headerName: 'Source',
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value as string}
          color={triggerColor[params.value as ActivityEventTrigger] ?? 'default'}
          variant={'outlined'}
          size={'small'}
        />
      ),
    },
    {
      field: 'confidence',
      headerName: 'Confidence',
      flex: 0.6,
      renderCell: (params) => `${Math.round(((params.value as number) ?? 0) * 100)}%`,
    },
    {
      field: 'people',
      headerName: 'People',
      flex: 1.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => <PeopleCell people={params.value as ActivityPersonSighting[]} />,
    },
    {
      field: 'capturesCount',
      headerName: 'Clips',
      flex: 0.5,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row as { id: string; capturesCount: number; videoS3Key: string | null };
        // Events written before per-capture archiving have no rows but may
        // still have the single attributed clip.
        if (row.capturesCount === 0 && !row.videoS3Key) {
          return null;
        }
        return (
          <Tooltip
            arrow
            title={row.capturesCount > 0 ? `View all ${row.capturesCount} snippets` : 'View clip'}
          >
            <IconButton size={'small'} onClick={() => setPlayingId(row.id)} aria-label={'View snippets'}>
              <Stack direction={'row'} alignItems={'center'} spacing={0.25}>
                <VideoLibraryIcon fontSize={'small'} />
                {row.capturesCount > 0 && (
                  <Typography variant={'caption'} color={'text.secondary'}>
                    {row.capturesCount}
                  </Typography>
                )}
              </Stack>
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'reviewStatus',
      headerName: 'Review',
      flex: 1.4,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as {
          id: string;
          reviewStatus: 'accepted' | 'rejected' | null;
          reviewCorrectedCode: string | null;
        };
        const event = eventById.get(row.id);
        const reviewed = row.reviewStatus !== null;
        const statusChip = row.reviewStatus === 'accepted' ? (
          <Chip label="Accepted" color="success" variant="outlined" size="small" />
        ) : row.reviewStatus === 'rejected' ? (
          <Chip
            label={`Rejected → ${row.reviewCorrectedCode ?? ''}`}
            color="error"
            variant="outlined"
            size="small"
          />
        ) : null;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {statusChip}
            <Stack direction="row" spacing={0.25}>
              <Tooltip title={reviewed ? 'Change to accepted' : 'Accept'}>
                <IconButton size="small" color="success" onClick={() => acceptEvent(row.id)}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={reviewed ? 'Change — pick a different code' : 'Reject — pick a different code'}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => event && setRejecting(event)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        );
      },
    },
  ], [eventById]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={'row'} spacing={2} sx={{ mb: 2 }} alignItems={'center'}>
        <Typography variant={'body2'} color={'text.secondary'}>
          {rows.length} event{rows.length === 1 ? '' : 's'}
        </Typography>
        <DateTimePicker
          label={'From'}
          value={from}
          onChange={setFrom}
          slotProps={{
            textField: {
              size: 'small',
              required: true,
              error: !from?.isValid,
              helperText: !from?.isValid ? 'Required' : ' ',
            },
          }}
        />
        <DateTimePicker
          label={'To'}
          value={to}
          onChange={setTo}
          minDateTime={from ?? undefined}
          slotProps={{
            textField: {
              size: 'small',
              required: true,
              error: !to?.isValid || (rangeValid ? false : !!from?.isValid),
              helperText:
                !to?.isValid
                  ? 'Required'
                  : !rangeValid && from?.isValid
                    ? 'Must be after From'
                    : ' ',
            },
          }}
        />
        <Tooltip arrow title={'Only suggestions the AI was unsure about, or where the rules overrode its code'}>
          <Chip
            size={'small'}
            icon={<PriorityHighIcon />}
            label={`High priority only (${highPriorityCount})`}
            color={highPriorityOnly ? 'warning' : 'default'}
            variant={highPriorityOnly ? 'filled' : 'outlined'}
            onClick={() => setHighPriorityOnly((current) => !current)}
            sx={{ ml: 'auto' }}
          />
        </Tooltip>
        <Tooltip arrow title={'Refresh'}>
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching || !rangeValid}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Table
        columns={columns}
        rows={rows}
        pageSize={Math.min(limit, 25)}
        isLoading={isLoading}
        NoRowsOverlayText={
          rangeValid
            ? 'No activity events in this range. AI monitoring writes a row per cadence tick + motion event during monitoring hours.'
            : 'Pick a valid From/To range to load events.'
        }
      />

      {hasNextPage ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button onClick={() => fetchNextPage()} disabled={isFetching}>
            Load more
          </Button>
        </Box>
      ) : null}

      <ActivityCaptureGallery eventId={playingId} onClose={() => setPlayingId(null)} />

      {rejecting && (
        <ActivityRejectDialog
          event={rejecting}
          onClose={() => setRejecting(null)}
          onReviewed={() => {
            setRejecting(null);
            refetch();
          }}
        />
      )}
    </Box>
  );
};

/**
 * The roster for one check-in. Deliberately shows every person seen on every
 * camera, not just the coded one — a second person in the house is the context
 * a monitor needs to judge whether the AI coded the right individual.
 */
const PeopleCell: FC<{ people: ActivityPersonSighting[] }> = ({ people }) => {
  if (people.length === 0) {
    return (
      <Typography variant={'caption'} color={'text.disabled'}>
        —
      </Typography>
    );
  }
  return (
    <Stack sx={{ py: 0.5, minWidth: 0 }}>
      {people.slice(0, 3).map((person, i) => (
        <Tooltip
          key={`${person.cameraId}-${person.label}-${i}`}
          arrow
          title={`${person.cameraName}: ${person.label} — ${person.description}`}
        >
          <Typography variant={'caption'} color={'text.secondary'} noWrap>
            <Box component={'span'} sx={{ color: 'text.disabled' }}>{person.cameraName}</Box>
            {' · '}
            {person.description}
          </Typography>
        </Tooltip>
      ))}
      {people.length > 3 && (
        <Typography variant={'caption'} color={'text.disabled'}>
          +{people.length - 3} more
        </Typography>
      )}
    </Stack>
  );
};

export default ActivityEventsTable;
